/* ==========================================================================
   HerLoom Hair Plan — questions, contenu et calcul
   --------------------------------------------------------------------------
   Écrit à la main. Remplace la transposition du dossier ChatGPT, qui produisait
   un plan défensif : il expliquait surtout ce qu'il ne pouvait pas affirmer, et
   suspendait la chaleur sur un produit chauffant.

   Ce catalogue part de l'inverse : apprendre quelque chose de vrai à la
   cliente. Chaque fait vérifiable porte sa source. Les quatre qui structurent
   le contenu :

   - Au-delà de ~200 °C la kératine se dégrade nettement ; la plage
     professionnelle est 160-200 °C.  (Christian et al., PubMed 21635854)
   - Sur cheveux humides, au-delà de ~125 °C l'eau interne se vaporise et creuse
     des cavités permanentes dans la fibre — « bubble hair ».
   - Le dommage vient des cycles dilatation/contraction de la cuticule : c'est
     le nombre de passages qui compte, pas seulement la température.
   - Une protection thermique réduit d'environ 50 %, en ralentissant le
     transfert. Elle ne l'annule pas.

   Ce qu'on n'écrit jamais : « damage-free », « répare », « stoppe la casse »,
   un expert nommé ou une étude inventée. Pas par prudence — parce qu'une
   promesse invérifiable est plus faible que « 160 °C au lieu de 200 ».
   ========================================================================== */

(function () {
  'use strict';

  const VERSION = 'herloom-brush-plan-v2';

  /* ---------------------------------------------------------------- */
  /* Sources                                                           */
  /* ---------------------------------------------------------------- */

  const SOURCES = {
    thermal: { title: 'Christian et al., Int. J. Cosmetic Science — thermal damage from flat ironing',
               url: 'https://pubmed.ncbi.nlm.nih.gov/21635854/' },
    bubble:  { title: 'Bubble hair deformity — heat on wet hair',
               url: 'https://en.wikipedia.org/wiki/Bubble_hair_deformity' },
    protect: { title: 'Lab Muffin Beauty Science — how heat protectants work',
               url: 'https://labmuffin.com/how-do-heat-protectant-hair-products-work/' },
    aadHair: { title: 'American Academy of Dermatology — tips for healthy hair',
               url: 'https://www.aad.org/public/everyday-care/hair-scalp-care/hair/healthy-hair-tips' },
    aadCurl: { title: 'American Academy of Dermatology — curly hair care',
               url: 'https://www.aad.org/public/everyday-care/hair-scalp-care/hair/curly-hair-care' },
    aadSun:  { title: 'American Academy of Dermatology — summer hair care',
               url: 'https://www.aad.org/public/everyday-care/hair-scalp-care/hair/summer-hair-care' }
  };

  /* ---------------------------------------------------------------- */
  /* Questions — 11 écrans                                             */
  /* ---------------------------------------------------------------- */

  /* Le 4e argument est la clef d'icone, resolue par OPT_ICONS dans
     herloom-hair-plan.js. La question 'shade' n'en a pas : ses pastilles de
     couleur sont deja son visuel. */
  const o = (id, label, note, icon) => ({ id, label, note, i: icon });

  const QUESTIONS = [
    { id: 'length', screen: 1, multi: false,
      label: 'How long is your hair right now?', options: [
        o('pixie', 'Pixie cut', 'Above the ear', 'len1'),
        o('short_bob', 'Short, above the chin', '', 'len2'),
        o('chin_bob', 'Chin-length bob', '', 'len3'),
        o('shoulder', 'Shoulder length', '', 'len4'),
        o('long', 'Past the shoulders', '', 'len5')] },

    { id: 'shape', screen: 2, multi: false,
      label: 'What does your hair do on its own?',
      help: 'Air-dried, no product, no brushing.', options: [
        o('straight', 'Stays straight', '', 'straight'),
        o('wavy', 'Waves up', 'S-shaped bends', 'wavy'),
        o('curly', 'Curls', 'Defined spirals', 'curly'),
        o('coily', 'Coils tightly', 'Tight curls or zig-zags', 'coily')] },

    { id: 'strand', screen: 3, multi: false,
      label: 'How thick is one single hair?',
      help: 'Pull one out and roll it between your fingers. Barely there? Fine. Wiry? Thick.',
      options: [
        o('fine', 'Fine', 'Hard to feel', 'thin'),
        o('medium', 'Medium', 'You can feel it', 'mid'),
        o('coarse', 'Thick', 'Wiry, holds its own shape', 'thick'),
        o('unknown', "I can't tell", '', 'question')] },

    { id: 'shade', screen: 4, multi: false, type: 'shade',
      label: 'What shade is your hair right now?' },

    { id: 'treatments', screen: 4, multi: true, exclusive: ['none'],
      label: 'Is any of this still in your hair?',
      help: 'Even if it was done months ago — it stays until it grows out.',
      options: [
        o('none', 'Nothing, my natural colour', '', 'leaf'),
        o('color', 'Colour or dye', '', 'palette'),
        o('highlights', 'Highlights or balayage', '', 'streaks'),
        o('bleach', 'Bleach', 'Lifted several shades', 'sparkle'),
        o('perm', 'Perm', '', 'rod'),
        o('relaxer', 'Keratin or chemical straightening', '', 'flatten')] },

    { id: 'condition', screen: 5, multi: true, exclusive: ['good'],
      label: 'How do your lengths and ends behave?', options: [
        o('good', 'Fine, no complaints', '', 'checkCircle'),
        o('dry', 'Dry or rough', '', 'noDrop'),
        o('tangles', 'Tangles a lot', '', 'knot'),
        o('breakage', 'Snaps or splits', '', 'split')] },

    { id: 'scalp', screen: 5, multi: false,
      label: 'And your scalp between washes?', options: [
        o('comfortable', 'Comfortable', '', 'smile'),
        o('oily', 'Gets oily fast', '', 'drops2'),
        o('dry', 'Feels tight or dry', '', 'flakes')] },

    { id: 'goal', screen: 6, multi: false,
      label: 'What do you want most?', options: [
        o('smooth', 'Smooth and sleek', 'Less frizz, more shine', 'sleek'),
        o('volume', 'Volume at the root', 'Without the flat look', 'lift'),
        o('shape', 'A cleaner shape', 'My cut, properly finished', 'scissors'),
        o('fast', 'A faster morning', 'Same result, less time', 'bolt')] },

    { id: 'obstacle', screen: 7, multi: false,
      label: "What's the hard part right now?", options: [
        o('back', 'The back of my head', '', 'rotate'),
        o('frizz', 'Frizz comes straight back', '', 'frizz'),
        o('drop', "It doesn't hold", '', 'falls'),
        o('technique', "I'm not sure I'm doing it right", '', 'question'),
        o('damage', "I'm worried about the heat", '', 'flame'),
        o('time', 'It takes too long', '', 'hourglass')] },

    { id: 'heatUse', screen: 8, multi: false,
      label: 'How often do you use heat today?', options: [
        o('never', 'Never', '', 'cal0'),
        o('sometimes', 'Now and then', '', 'cal1'),
        o('weekly', 'A few times a week', '', 'cal3'),
        o('daily', 'Most days', '', 'cal6')] },

    { id: 'products', screen: 9, multi: true, exclusive: ['none'],
      label: "What's already in your bathroom?", options: [
        o('conditioner', 'Conditioner', '', 'bottle'),
        o('mask', 'Hair mask', '', 'jar'),
        o('leavein', 'Leave-in', '', 'spray'),
        o('protectant', 'Heat protectant', '', 'shield'),
        o('oil', 'Hair oil or serum', '', 'dropper'),
        o('none', 'None of these', '', 'noCircle')] },

    { id: 'wash', screen: 10, multi: false,
      label: 'How often do you wash?', options: [
        o('daily', 'Every day', '', 'drops3'),
        o('every2_3', 'Every 2–3 days', '', 'drops2'),
        o('weekly', 'About weekly', '', 'drop1'),
        o('less', 'Less than weekly', '', 'noDrop')] },

    { id: 'environment', screen: 11, multi: true, exclusive: ['none'],
      label: 'Any of these part of your week?', options: [
        o('sun', 'A lot of sun', '', 'sun'),
        o('pool', 'Swimming pool', '', 'pool'),
        o('sea', 'Sea swimming', '', 'waves'),
        o('sweat', 'Sport or sweating', '', 'dumbbell'),
        o('none', 'None of these', '', 'noCircle')] }
  ];

  const SCREENS = [['length'], ['shape'], ['strand'], ['shade', 'treatments'],
                   ['condition', 'scalp'], ['goal'], ['obstacle'], ['heatUse'],
                   ['products'], ['wash'], ['environment']];

  /* ---------------------------------------------------------------- */
  /* Cartes                                                            */
  /* ---------------------------------------------------------------- */

  const c = (id, title, body, source) => [id, { id, title, body, source: source || null }];

  const CARDS = Object.fromEntries([

    /* ---- Insight : une seule, la plus pertinente ------------------ */

    c('iBleach', 'Lightened hair takes heat faster than you think',
      'Bleaching and heavy highlighting strip lipids out of the cuticle — the layer that normally slows heat on its way in. The same setting that a virgin hair shrugs off reaches the cortex of lightened hair sooner. This is why your plan starts you a step below where you might expect, and why one clean pass matters more for you than for anyone else.',
      'thermal'),

    c('iFine', 'Fine hair gets hot faster — that works in your favour',
      'A fine strand has less mass, so it reaches the tool temperature in a fraction of the time a thick strand needs. Most people read that as a warning. It is actually an advantage: you get your result at the lowest setting, which is exactly where hair keeps its condition. Higher heat would not make it faster, only harsher.',
      'thermal'),

    c('iFrizz', 'Frizz is a water problem, not a smoothness problem',
      'Frizz happens when the cuticle sits slightly open and pulls moisture out of the air. Heat that closes the cuticle in one controlled pass fixes it. Heat applied over and over does the opposite: each heat-and-cool cycle lifts the cuticle edges a little more, so hair that has been re-straightened five times frizzes faster than hair straightened once.',
      'thermal'),

    c('iPasses', 'It is the number of passes, not the number of days',
      'Cuticle damage is cumulative and it counts passes, not sessions. Every pass expands the cuticle then contracts it, and repeated cycles lift and delaminate the edges. Someone who styles daily with one pass at a moderate setting is treating their hair better than someone who styles weekly and goes over each section four times.',
      'thermal'),

    c('iVolume', 'Volume is decided in the first inch',
      'Lift comes from the direction the hair leaves the scalp, not from anything you do to the lengths. If the root is trained upward and away, the length follows. This is why working the mid-lengths for volume feels like effort with no result — you are shaping the part of the hair that was never going to hold the shape.'),

    c('iCurl', 'Smoothing a curl is not the same as flattening it',
      'Curly and coily hair is naturally drier, because sebum travels down a spiral much more slowly than down a straight shaft. That means your smoothing pass works best on hair that is already conditioned and detangled — dry curls fight the brush, and fighting it is what causes breakage, not the heat itself.',
      'aadCurl'),

    c('iBreakage', 'Damage does not reverse — but it does stop',
      'Split and snapped ends cannot be repaired; no product reseals a broken fibre. What you can do is stop adding to it. Lower setting, fewer passes, and never on damp hair. The damaged length grows out; the new length coming in is entirely up to how you treat it from here.'),

    c('iHeatCurious', 'You do not need the temperature you have been sold',
      'A standard flat iron starts around 200 °C and many go past 230 °C. Research on thermal damage puts significant keratin degradation above roughly 200 °C, with the professional working range sitting at 160–200 °C. Almost everyone is styling hotter than they need to, because the tool offered it — not because the hair required it.',
      'thermal'),

    /* ---- Chaleur --------------------------------------------------- */

    c('hWet', 'Dry means dry — this is the one that is permanent',
      'Above about 125 °C, water still inside the hair turns to steam and blows microscopic cavities through the fibre. The shaft ends up spongy from the inside, and it does not heal: the only way out is cutting it off. Ten extra minutes of drying beats six months of growing it back. If a section feels even slightly cool to the touch, it is still wet.',
      'bubble'),

    c('hProtect', 'What a heat protectant actually does',
      'It is not a shield. Silicones and similar film-formers have low thermal conductivity, so they slow the rate at which heat crosses into the fibre and they hold moisture in. Measured reduction lands around 50 % depending on the formula — real, worth using, and not a licence to turn the dial up. Apply it to damp hair, then dry fully before any heated tool.',
      'protect'),

    c('hProtectMissing', 'The one product worth adding',
      'You did not list a heat protectant. It is the single highest-return item for anyone using a heated tool: roughly half the heat load, for the cost of a spray. Apply on damp hair, comb it through so it is even, then dry completely.',
      'protect'),

    /* ---- Méthode --------------------------------------------------- */

    c('mSection', 'Section size is the setting nobody adjusts',
      'Take a section thicker than the brush can heat through and the outside gets the full temperature while the inside gets almost none — so you go back over it, and that second pass is where the damage lives. Roughly an inch, thin enough to see through when you hold it up. Smaller sections at a lower setting always beat bigger sections at a higher one.'),

    c('mOnePass', 'One slow pass, not three fast ones',
      'Move at the speed where you can count to three from root to end. It feels too slow the first time. The alternative is going over the same hair repeatedly, and every extra pass is another expansion-and-contraction cycle on a cuticle that only has so many left in it.',
      'thermal'),

    c('mCool', 'Hair sets as it cools, not as it heats',
      'The shape locks in during the few seconds after the heat leaves. If you brush, touch or tie a section while it is still warm, you undo the pass you just made. Finish a section, leave it alone, move to the next one — by the time you come back it has set itself.'),

    c('mRoot', 'For lift: leave the root, come back to it',
      'Style the lengths first, then go back and lift only the roots at the crown and the parting, guiding each section up and away from the scalp before you move down. Doing the roots first means you flatten them again on every pass that follows.'),

    c('mBack', 'The two-mirror fix for the back',
      'Nearly everyone works the back blind and it shows: uneven sections and repeated passes on the same spot. Set a hand mirror so you can see the reflection of the back in your main mirror before you switch the brush on. Then split the back into a left half and a right half and finish one before starting the other.'),

    c('mFrizzFinish', 'Finish downward, always',
      'The cuticle lies like roof tiles, pointing from root to end. Every pass that travels downward closes them; anything that runs upward lifts them and that is what catches the light as frizz. Keep the brush travelling in one direction and never work back up a section to "fix" it.'),

    c('mShort', 'Short hair: think in small panels',
      'On a short cut the brush head is often wider than the section you are trying to control. Work in narrow panels and accept that the shortest pieces around the hairline are shaped by the cut, not by the tool. Chasing them costs you more than it gives.'),

    c('mCoarse', 'Thick hair: more sections, not more heat',
      'A thick strand carries more mass, so it needs longer contact — not hotter contact. The way to give it longer contact safely is smaller sections and a slower hand. Turning the dial up is the shortcut that costs condition.'),

    c('mFast', 'The 80/20 of a fast morning',
      'You do not need to style all of it. The front section and the parting are what people see and what you see in the mirror. Do those properly and leave the back as it fell — most days nobody, including you, will notice the difference.'),

    /* ---- Entretien -------------------------------------------------- */

    c('wOily', 'Wash the scalp, condition the lengths',
      'Oily roots and dry ends are the same routine done in the wrong places. Shampoo belongs on the scalp, where the oil actually is; conditioner belongs from mid-length down, where the oil never reaches. Most people do both to all of it and wonder why the ends stay dry.',
      'aadHair'),

    c('wDry', 'A tight scalp is usually over-washing',
      'If your scalp feels tight after washing, the routine is stripping more than it needs to. Stretch the interval by a day and see what happens before you buy anything. Scalp comfort is the signal to follow, not a schedule.',
      'aadHair'),

    c('wDetangleCurly', 'Detangle wet, with conditioner in',
      'Curls tangle at the bends, and dry detangling snaps them there. Work conditioner through, use a wide-tooth comb or your fingers, and start at the ends moving upward. Detangling and heat styling are separate jobs — never let the heated brush be the thing that finds a knot.',
      'aadCurl'),

    c('wDetangleStraight', 'Start at the ends and work up',
      'Pulling a comb from the roots down drags every knot into a bigger one at the bottom. Take the last few inches first, then move your hand up an inch at a time. It takes twenty seconds longer and costs you nothing in breakage.'),

    c('wSleep', 'Half your frizz happens while you sleep',
      'Friction against cotton lifts the cuticle all night. A loose braid, a silk or satin pillowcase, or both, and you wake up with a style that still needs a touch-up rather than a redo.'),

    c('wSun', 'UV bleaches hair the same way it bleaches everything',
      'Sun oxidises the pigment and degrades surface proteins, and lighter or grey hair has less pigment to absorb the hit. A hat during long exposure does more than any product marketed for it.',
      'aadSun'),

    c('wPool', 'Rinse before you swim, not just after',
      'Hair absorbs water like a sponge. If it is already full of clean water, it has far less room for chlorinated water. Wet it at the shower before you get in, and rinse again as soon as you are out.',
      'aadSun'),

    c('wSweat', 'Sweat is salt, and salt is drying',
      'You do not necessarily need a full wash after every session, but leaving dried sweat at the scalp overnight is what turns into itch. Rinse with water, dry properly, and save the shampoo for when the scalp actually asks for it.'),

    c('wDevice', 'Clean the plates, not just the bristles',
      'Product residue bakes onto a heated surface and changes how evenly it transfers. Let it cool completely, wipe the plates with a barely damp cloth, and never put the body of the brush under a tap.'),

    /* ---- Erreurs ---------------------------------------------------- */

    c('eHotter', 'Turning it up when it is not working',
      'When a section refuses to behave, the cause is almost always that it is too thick or still slightly damp. Temperature is the last thing to change, not the first. Take a thinner section at the same setting and it usually solves itself.'),

    c('eRepeat', 'Going over the same section again',
      'The second pass does far less for the look than it does to the cuticle. If the first pass did not deliver, change the section size or slow the hand — repeating the same move at the same speed just spends condition for nothing.',
      'thermal'),

    c('eDamp', 'Starting on hair that is "almost dry"',
      'Almost dry is wet where it matters, at the core of the fibre. That is precisely the water that turns to steam. Check the roots and the underneath layer with the back of your hand before you start.',
      'bubble'),

    c('eStill', 'Letting the brush sit in one place',
      'A stationary tool concentrates its entire output on a couple of centimetres of hair. Keep it travelling, even when you slow down.'),

    c('eForce', 'Pushing through a knot',
      'If the brush stops, it has found a tangle. Forcing it snaps hair at that exact point. Stop, free it with your fingers or a comb, then continue.'),

    c('eProducts', 'Banning ingredients because of your colour',
      'Blonde, grey or fine hair does not by itself rule out silicones, sulfates, proteins or keratin. Judge the product on how your hair looks and feels after a few uses, not on a category rule someone posted online.'),

    /* ---- Dépannage --------------------------------------------------- */

    c('tFlat', 'If it went flat',
      'Two usual causes: the roots were styled before the lengths, so later passes pressed them down; or product was applied at the root where it weighs the lift out. Style the lengths first, lift the roots last, and keep anything creamy below the ears.'),

    c('tFrizzBack', 'If the frizz came back within the hour',
      'Either a pass ran upward at some point, or the hair was not fully dry when you started. Both leave the cuticle open, and an open cuticle pulls moisture from the air within minutes of hitting humidity.'),

    c('tUneven', 'If the back looks different from the front',
      'It nearly always does when the back is worked blind. Set the second mirror, halve the back, and give each half the same section size you used at the front.'),

    c('tDrop', 'If it drops out by lunchtime',
      'The section was probably still warm when it was touched, brushed or tied. Let each section cool completely in the shape you gave it before anything touches it — including your hands.')
  ]);

  /* ---------------------------------------------------------------- */
  /* Palette — miroir de celle de l'Air Styler                         */
  /* Vérifiée par check-shades.py contre herloom-style-id.js           */
  /* ---------------------------------------------------------------- */

  const SHADE_TO_PLAN = {
    platinum_blonde: 'light', blonde: 'light', dark_blonde: 'light',
    light_brown: 'dark', brown: 'dark', dark_brown: 'dark', black: 'dark',
    copper: 'red', red: 'red', other: 'gray'
  };


const SHADES = [
    { id: 'blonde', label: 'Blonde', swatch: '#DCC9A8', tones: [
      { v: 'platinum',       l: 'Platinum',        hex: '#EDE4D4', maps: 'platinum_blonde' },
      { v: 'ash_blonde',     l: 'Ash blonde',      hex: '#CFC3AC', maps: 'blonde' },
      { v: 'beige_blonde',   l: 'Beige blonde',    hex: '#DCC9A8', maps: 'blonde' },
      { v: 'golden_blonde',  l: 'Golden blonde',   hex: '#D6B173', maps: 'blonde' },
      { v: 'honey',          l: 'Honey',           hex: '#C29553', maps: 'dark_blonde' },
      { v: 'strawberry',     l: 'Strawberry',      hex: '#C98F6A', maps: 'dark_blonde' }
    ] },
    { id: 'chestnut', label: 'Chestnut', swatch: '#A9754A', tones: [
      { v: 'bronde',         l: 'Bronde',          hex: '#937A5B', maps: 'dark_blonde' },
      { v: 'mushroom',       l: 'Mushroom',        hex: '#8A7C6E', maps: 'light_brown' },
      { v: 'light_chestnut', l: 'Light chestnut',  hex: '#A9855F', maps: 'light_brown' },
      { v: 'caramel',        l: 'Caramel',         hex: '#A9754A', maps: 'light_brown' },
      { v: 'chestnut',       l: 'Chestnut',        hex: '#8C6647', maps: 'brown' }
    ] },
    { id: 'brunette', label: 'Brunette', swatch: '#5A3B26', tones: [
      { v: 'light_brown',    l: 'Light brown',     hex: '#6F4E33', maps: 'brown' },
      { v: 'mocha',          l: 'Mocha',           hex: '#4A362A', maps: 'brown' },
      { v: 'medium_brown',   l: 'Medium brown',    hex: '#5A3B26', maps: 'brown' },
      { v: 'chocolate',      l: 'Chocolate',       hex: '#43291A', maps: 'dark_brown' },
      { v: 'espresso',       l: 'Espresso',        hex: '#2E1D13', maps: 'dark_brown' }
    ] },
    { id: 'red', label: 'Red & copper', swatch: '#B25A28', tones: [
      { v: 'strawberry_red', l: 'Strawberry red',  hex: '#C4744A', maps: 'red' },
      { v: 'copper',         l: 'Copper',          hex: '#B25A28', maps: 'copper' },
      { v: 'ginger',         l: 'Ginger',          hex: '#A8481E', maps: 'copper' },
      { v: 'auburn',         l: 'Auburn',          hex: '#7E3520', maps: 'red' },
      { v: 'burgundy',       l: 'Burgundy',        hex: '#5C1F22', maps: 'red' }
    ] },
    { id: 'black', label: 'Black', swatch: '#12100F', tones: [
      { v: 'soft_black',     l: 'Soft black',      hex: '#241C18', maps: 'black' },
      { v: 'jet_black',      l: 'Jet black',       hex: '#12100F', maps: 'black' },
      { v: 'blue_black',     l: 'Blue black',      hex: '#16181D', maps: 'black' }
    ] },
    { id: 'grey', label: 'Grey & silver', swatch: '#B9B6B1', tones: [
      { v: 'salt_pepper',    l: 'Salt and pepper', hex: '#6E6A66', maps: 'other' },
      { v: 'silver',         l: 'Silver',          hex: '#B9B6B1', maps: 'other' },
      { v: 'pearl_grey',     l: 'Pearl grey',      hex: '#D3D0CB', maps: 'other' },
      { v: 'white',          l: 'White',           hex: '#EAE8E4', maps: 'other' }
    ] }
  ];

  const SHADE_INDEX = {};
  for (const fam of SHADES) for (const tone of fam.tones)
    SHADE_INDEX[tone.v] = { family: fam.id, label: tone.l, hex: tone.hex,
                            plan: SHADE_TO_PLAN[tone.maps] || 'unknown' };

  /* ---------------------------------------------------------------- */
  /* Calcul                                                            */
  /* ---------------------------------------------------------------- */

  /* Trois réglages réels de l'appareil. Le fabricant les associe à une
     épaisseur ; on part de là, puis on descend d'un cran si la fibre a été
     éclaircie ou si elle casse. On ne monte jamais au-dessus de 180 par
     défaut : au-delà de 200 la dégradation de la kératine est nette, et
     laisser une marge est le seul choix défendable sans protocole écrit. */
  const SETTINGS = [
    { c: 160, f: 320, label: 'Fine hair' },
    { c: 180, f: 356, label: 'Medium hair' },
    { c: 200, f: 392, label: 'Thick hair' }
  ];

  function has(list, v) { return Array.isArray(list) && list.indexOf(v) >= 0; }

  function pickHeat(a) {
    let i = a.strand === 'coarse' ? 2 : a.strand === 'medium' ? 1 : 0;
    const lightened = has(a.treatments, 'bleach') || has(a.treatments, 'highlights');
    const breakage = has(a.condition, 'breakage');
    const moved = [];

    if (a.strand === 'unknown') i = 0;
    if (lightened && i > 0) { i -= 1; moved.push('lightened'); }
    if (breakage && i > 0) { i -= 1; moved.push('breakage'); }
    if (i > 1) { i = 1; moved.push('cap'); }

    /* Une phrase, pas une liste de motifs — et elle porte toujours la
       comparaison au fer à lisser, qui est l'argument. */
    const base =
      a.strand === 'unknown'
        ? 'You were not sure how thick your hair is, so this starts at the gentlest setting — the one that is hardest to get wrong.'
      : a.strand === 'fine'
        ? 'Fine hair reaches the tool temperature faster than thick hair, so the lowest setting already does the work.'
      : a.strand === 'medium'
        ? 'Medium hair needs a little more contact than fine hair, which is what this setting gives you.'
        : 'Thick hair carries more mass, so it needs longer contact — which you get from smaller sections, not a hotter tool.';

    const adjust =
      moved.indexOf('lightened') >= 0 && moved.indexOf('breakage') >= 0
        ? ' You reported lightening and breakage, so this sits two steps below the default for your thickness.'
      : moved.indexOf('lightened') >= 0
        ? ' Because your hair has been lightened, the cuticle lets heat through faster, so you start one step below.'
      : moved.indexOf('breakage') >= 0
        ? ' Because your ends are snapping, you start one step below and let the new growth come in undamaged.'
      : moved.indexOf('cap') >= 0
        ? ' We hold the starting point here rather than at the top setting: past roughly 200 °C, keratin degradation gets steep.'
        : '';

    const compare = ' For reference, a standard flat iron starts around 200 °C and many run past 230 °C. You do not need to be there.';
    return { index: i, setting: SETTINGS[i], why: base + adjust + compare };
  }

  function buildPlan(a) {
    const goal = a.goal;
    const lightened = has(a.treatments, 'bleach') || has(a.treatments, 'highlights');
    const breakage = has(a.condition, 'breakage');
    const textured = a.shape === 'curly' || a.shape === 'coily';
    const short = a.length === 'pixie' || a.length === 'short_bob';
    const heat = pickHeat(a);

    /* Une seule carte pédagogique, la plus parlante pour ce profil. */
    const insight =
      breakage ? 'iBreakage' :
      lightened ? 'iBleach' :
      a.obstacle === 'damage' || a.heatUse === 'daily' ? (a.obstacle === 'damage' ? 'iHeatCurious' : 'iPasses') :
      a.obstacle === 'frizz' || goal === 'smooth' ? 'iFrizz' :
      textured ? 'iCurl' :
      goal === 'volume' ? 'iVolume' :
      a.strand === 'fine' ? 'iFine' : 'iPasses';

    /* Gratuit : deux faits en plus, choisis pour être nouveaux. */
    const freeFacts = ['hWet'];
    freeFacts.push(has(a.products, 'protectant') ? 'hProtect' : 'hProtectMissing');

    /* Méthode — le morceau vendu. */
    const method = ['mSection', 'mOnePass', 'mCool'];
    if (goal === 'volume') method.push('mRoot');
    if (goal === 'smooth' || a.obstacle === 'frizz') method.push('mFrizzFinish');
    if (goal === 'fast' || a.obstacle === 'time') method.push('mFast');
    if (a.obstacle === 'back') method.push('mBack');
    if (short) method.push('mShort');
    if (a.strand === 'coarse') method.push('mCoarse');

    const care = [];
    if (a.scalp === 'oily') care.push('wOily');
    if (a.scalp === 'dry') care.push('wDry');
    care.push(textured ? 'wDetangleCurly' : 'wDetangleStraight');
    care.push('wSleep');
    if (has(a.environment, 'sun')) care.push('wSun');
    if (has(a.environment, 'pool')) care.push('wPool');
    if (has(a.environment, 'sea')) care.push('wPool');
    if (has(a.environment, 'sweat')) care.push('wSweat');
    care.push('wDevice');

    const mistakes = ['eHotter', 'eDamp', 'eRepeat'];
    if (has(a.condition, 'tangles') || textured) mistakes.push('eForce');
    else mistakes.push('eStill');
    mistakes.push('eProducts');

    const trouble = [];
    if (goal === 'volume' || a.obstacle === 'drop') trouble.push('tFlat');
    if (goal === 'smooth' || a.obstacle === 'frizz') trouble.push('tFrizzBack');
    if (a.obstacle === 'back') trouble.push('tUneven');
    if (a.obstacle === 'drop') trouble.push('tDrop');
    if (!trouble.length) trouble.push('tFrizzBack', 'tFlat');

    const uniq = list => [...new Set(list)];
    const sections = {
      insight: [insight],
      facts: uniq(freeFacts),
      method: uniq(method),
      care: uniq(care),
      mistakes: uniq(mistakes),
      trouble: uniq(trouble)
    };

    const used = Object.values(sections).flat();
    const sources = uniq(used.map(id => CARDS[id].source).filter(Boolean)).map(k => SOURCES[k]);

    return { version: VERSION, answers: a, goal, heat, sections, sources,
             lockedCount: sections.method.length + sections.care.length +
                          sections.mistakes.length + sections.trouble.length };
  }

  window.HHP = { VERSION, QUESTIONS, SCREENS, CARDS, SOURCES, SETTINGS,
                 SHADE_TO_PLAN, SHADES, SHADE_INDEX, buildPlan };
})();
